import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '../search/search-input.components';
import { Search } from '../images/png/icons';
import { searchNews } from '../../features/news/news.slice';
import { RootState, AppDispatch } from '../../store';
import { useDispatch, useSelector } from 'react-redux';

interface NewsSearchProps {
  onSearchResults?: (results: any) => void;
  onSearchQuery?: (query: string) => void;
  currentPage?: number;
  itemsPerPage?: number;
}

export interface NewsSearchRef {
  searchWithPage: (page: number) => void;
}

export const NewsSearch = forwardRef<NewsSearchRef, NewsSearchProps>(({ 
  onSearchResults, 
  onSearchQuery,
  currentPage = 1,
  itemsPerPage = 10
}, ref) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { searchResults, loading, error } = useSelector((state: RootState) => state.news);

  // Function to perform search API call
  const performSearch = async (query: string, page: number = 1) => {
    if (query.trim().length > 2 && !isSearching) {
      setIsSearching(true);
      setLastSearchQuery(query.trim());
      try {
        const payload = {
          searchText: query.trim(),
          pageNumber: page,
          pageSize: itemsPerPage, 
        };
        
        console.log('Searching with payload:', payload); // Debug log
        console.log('Expected API URL:', `NewsArticle?searchText=${encodeURIComponent(query.trim())}&pageNumber=${page}&pageSize=${itemsPerPage}`); // Debug log
        const result = await dispatch(searchNews(payload));
        console.log('Search result:', result); // Debug log
        
        if (onSearchResults && result.payload) {
          // Ensure we pass the complete response with pagination info
          const searchResponse = {
            ...result.payload,
            currentPage: page,
            searchQuery: query.trim()
          };
          onSearchResults(searchResponse);
        }
        
        if (onSearchQuery) {
          onSearchQuery(query.trim());
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    }
  };

  // Expose method to parent component
  useImperativeHandle(ref, () => ({
    searchWithPage: (page: number) => {
      if (lastSearchQuery.length > 2 && !isSearching) {
        performSearch(lastSearchQuery, page);
      }
    }
  }));

  useEffect(() => {
    if (searchQuery.trim().length > 2) {
      const delayedSearch = setTimeout(() => {
        performSearch(searchQuery, 1); // Always start from page 1 for new searches
      }, 500);

      return () => clearTimeout(delayedSearch);
    } else if (searchQuery.trim().length === 0) {
      onSearchResults?.(null);
      onSearchQuery?.('');
    }
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // The search is already handled by the useEffect with debouncing
  };

  return (
    <section className="news-search">
      <Container>
        <div className="news-search-container">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSubmit={handleSearch}
            placeholder={t("SEARCH_PLACEHOLDER")}
            iconSrc={Search}
            altText={t("ALT_SEARCH")}
            className="news-search-form"
            inputClassName="news-search-input"
          />
          {(isSearching || loading) && (
            <div className="search-loading">
              <small className="text-muted">{t("SEARCHING", "Searching...")}</small>
            </div>
          )}
        </div>
        {searchQuery.trim().length > 0 && searchQuery.trim().length <= 2 && (
          <div className="search-hint">
            <small className="text-danger">{t("SEARCH_MIN_CHARS", "Please enter at least 3 characters to search")}</small>
          </div>
        )}
        {error && (
          <div className="search-error">
            <small className="text-danger">{t("SEARCH_ERROR", "An error occurred while searching. Please try again.")}</small>
          </div>
        )}
      </Container>
    </section>
  );
});
